import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Skeleton } from '../../components/ui/skeleton';
import { LogIn, LogOut, TrendingUp, BarChart3, BedDouble, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const STATUS_COLORS = {
  confirmed: { bg: '#dcfce7', color: '#166534' },
  pending_payment: { bg: '#fef3c7', color: '#92400e' },
  cancelled: { bg: '#fee2e2', color: '#991b1b' },
  checked_in: { bg: '#dbeafe', color: '#1e40af' },
  checked_out: { bg: '#f3f4f6', color: '#374151' },
};

export default function AdminDashboard() {
  const { apiHeaders, t, language, formatPrice, BACKEND_URL } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reports/dashboard`, apiHeaders())
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [BACKEND_URL]);

  const statusLabel = {
    confirmed: t('status_confirmed'), pending_payment: t('status_pending_payment'),
    cancelled: t('status_cancelled'), checked_in: t('status_checked_in'), checked_out: t('status_checked_out'),
  };

  const kpis = data ? [
    { icon: LogIn, label: t('today_checkins'), value: data.checkins_today, bg: '#dcfce7', color: '#166534', testid: 'admin-kpi-checkins' },
    { icon: LogOut, label: t('today_checkouts'), value: data.checkouts_today, bg: '#fef3c7', color: '#92400e', testid: 'admin-kpi-checkouts' },
    { icon: BarChart3, label: t('occupancy'), value: `${data.occupancy_percentage}%`, bg: '#dbeafe', color: '#1e40af', testid: 'admin-kpi-occupancy' },
    { icon: TrendingUp, label: t('monthly_revenue'), value: formatPrice(data.monthly_revenue_try), bg: '#EFE7DA', color: '#1F3A2E', testid: 'admin-kpi-revenue' },
  ] : [];

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', fontWeight: 600, color: '#1F3A2E', marginBottom: '4px' }}>
          {t('dashboard')}
        </h1>
        <p style={{ fontSize: '13px', color: '#6E7F5B' }}>
          {new Date().toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {loading ? [1,2,3,4].map(i => <Skeleton key={i} style={{ height: '96px', borderRadius: '12px' }} />) :
          kpis.map(kpi => (
            <div key={kpi.label} style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '18px 20px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                <kpi.icon size={18} style={{ color: kpi.color }} />
              </div>
              <p style={{ fontSize: '22px', fontWeight: 800, color: '#1F3A2E', fontVariantNumeric: 'tabular-nums', marginBottom: '2px' }} data-testid={kpi.testid}>
                {kpi.value}
              </p>
              <p style={{ fontSize: '12px', color: '#6E7F5B' }}>{kpi.label}</p>
            </div>
          ))
        }
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px' }}>
        {/* Today's Activity */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Check-ins */}
          <div style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, color: '#1F3A2E' }}>
                <LogIn size={14} style={{ color: '#166534' }} /> {t('today_checkins')}
              </div>
              {!loading && data && (
                <span style={{ background: '#dcfce7', color: '#166534', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px' }}>{data.checkins_today}</span>
              )}
            </div>
            {loading ? <Skeleton style={{ height: '60px' }} /> :
              data?.todays_checkins?.length === 0 ? (
                <p style={{ fontSize: '12px', color: '#B0A99A', textAlign: 'center', padding: '8px 0' }}>
                  {language === 'tr' ? 'Bugün giriş yok' : 'No check-ins today'}
                </p>
              ) : data?.todays_checkins?.map(res => (
                <div key={res._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F6F1E7' }}>
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#1F3A2E' }}>{res.first_name} {res.last_name}</p>
                    <p style={{ fontSize: '11px', color: '#6E7F5B' }}>{res.room_name}</p>
                  </div>
                  <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', ...STATUS_COLORS[res.status] }}>
                    {statusLabel[res.status]}
                  </span>
                </div>
              ))
            }
          </div>

          {/* Check-outs */}
          <div style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, color: '#1F3A2E' }}>
                <LogOut size={14} style={{ color: '#92400e' }} /> {t('today_checkouts')}
              </div>
              {!loading && data && (
                <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px' }}>{data.checkouts_today}</span>
              )}
            </div>
            {loading ? <Skeleton style={{ height: '60px' }} /> :
              data?.todays_checkouts?.length === 0 ? (
                <p style={{ fontSize: '12px', color: '#B0A99A', textAlign: 'center', padding: '8px 0' }}>
                  {language === 'tr' ? 'Bugün çıkış yok' : 'No check-outs today'}
                </p>
              ) : data?.todays_checkouts?.map(res => (
                <div key={res._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F6F1E7' }}>
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#1F3A2E' }}>{res.first_name} {res.last_name}</p>
                    <p style={{ fontSize: '11px', color: '#6E7F5B' }}>{res.room_name}</p>
                  </div>
                </div>
              ))
            }
          </div>

          {/* Pending */}
          {!loading && data?.pending_payments > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AlertCircle size={16} style={{ color: '#d97706', flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: '13px', fontWeight: 700, color: '#92400e' }}>
                  {data.pending_payments} {language === 'tr' ? 'bekleyen ödeme' : 'pending payments'}
                </p>
                <button onClick={() => navigate('/admin/reservations')} style={{ fontSize: '11px', color: '#d97706', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                  {language === 'tr' ? 'Görüntüle →' : 'View →'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Recent Reservations */}
        <div style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, color: '#1F3A2E' }}>
              <BedDouble size={14} /> {t('recent_reservations')}
            </div>
            <button onClick={() => navigate('/admin/reservations')} style={{ fontSize: '12px', color: '#B08D57', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>
              {language === 'tr' ? 'Tümünü gör →' : 'View all →'}
            </button>
          </div>
          {loading ? <Skeleton style={{ height: '200px' }} /> :
            data?.recent_reservations?.length === 0 ? (
              <p style={{ fontSize: '13px', color: '#B0A99A', textAlign: 'center', padding: '40px 0' }}>
                {language === 'tr' ? 'Henüz rezervasyon yok' : 'No reservations yet'}
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }} data-testid="admin-reservations-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #EFE7DA' }}>
                      {[language === 'tr' ? 'Referans' : 'Ref', language === 'tr' ? 'Misafir' : 'Guest', language === 'tr' ? 'Oda' : 'Room', language === 'tr' ? 'Durum' : 'Status', language === 'tr' ? 'Tutar' : 'Amount'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data?.recent_reservations?.slice(0, 8).map(res => {
                      const sc = STATUS_COLORS[res.status] || { bg: '#f3f4f6', color: '#374151' };
                      return (
                        <tr key={res._id} style={{ borderBottom: '1px solid #F9F7F4', cursor: 'pointer' }} onClick={() => navigate('/admin/reservations')}>
                          <td style={{ padding: '10px 8px' }}>
                            <code style={{ fontSize: '11px', background: '#F6F1E7', padding: '2px 6px', borderRadius: '4px', color: '#3E5A3F' }}>{res.booking_ref}</code>
                          </td>
                          <td style={{ padding: '10px 8px' }}>
                            <p style={{ fontWeight: 600, color: '#1F3A2E', marginBottom: '1px' }}>{res.first_name} {res.last_name}</p>
                            <p style={{ fontSize: '11px', color: '#6E7F5B' }}>{res.check_in} → {res.check_out}</p>
                          </td>
                          <td style={{ padding: '10px 8px', color: '#5A5450', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{res.room_name}</td>
                          <td style={{ padding: '10px 8px' }}>
                            <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: sc.bg, color: sc.color, fontWeight: 600 }}>
                              {statusLabel[res.status] || res.status}
                            </span>
                          </td>
                          <td style={{ padding: '10px 8px', fontWeight: 700, color: '#1F3A2E', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                            {formatPrice(res.total_price_try)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
}
